import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { userService, expertService, authService } from '../api/services';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Camera, Save, Lock, Loader2, Eye, EyeOff, Video, Mic, MessageCircle, AlertTriangle, X } from 'lucide-react';
import toast from 'react-hot-toast';

const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];

const SESSION_TYPE_OPTIONS = [
  { id: 'VIDEO', icon: Video,         label: 'Video Call', desc: 'Face-to-face video' },
  { id: 'AUDIO', icon: Mic,           label: 'Audio Call', desc: 'Voice-only sessions' },
  { id: 'CHAT',  icon: MessageCircle, label: 'Text Chat',  desc: 'Real-time chat' },
];

const Section = ({ title, children }) => (
  <div className="bg-white border border-cream-200 rounded-2xl p-6 space-y-5">
    <h3 className="font-serif text-lg font-semibold text-forest-900 border-b border-cream-100 pb-3">{title}</h3>
    {children}
  </div>
);

const Field = ({ label, type = 'text', value, onChange, placeholder, hint }) => (
  <div>
    <label className="block text-xs font-medium text-forest-700 mb-1.5">{label}</label>
    <input type={type} value={value ?? ''} onChange={onChange} placeholder={placeholder}
      className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white" />
    {hint && <p className="text-xs text-forest-400 mt-1">{hint}</p>}
  </div>
);

const defaultSlots = { monday:[], tuesday:[], wednesday:[], thursday:[], friday:[], saturday:[], sunday:[] };

const parseAvailability = (raw) => {
  if (!raw) return defaultSlots;
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  try { return JSON.parse(raw); } catch { return defaultSlots; }
};

const parseJson = (val, fallback) => {
  if (Array.isArray(val)) return val;
  try { return val ? JSON.parse(val) : fallback; } catch { return fallback; }
};

const ENTERPRISE_TYPES = [
  'Tour Operation',
  'Travel Agency',
  'Accommodation',
  'Eco & Nature Tourism',
  'Culture',
  'Adventure Tourism',
  'Food & Culinary Tourism',
  'Tour Guiding',
  'Destination Marketing',
  'Creative Arts',
  'Tourism Content Creation',
  'Destination Development',
  'Design Thinking',
  'Other',
];

const SettingsPage = () => {
  const { user } = useAuth();
  const fileRef  = useRef(null);
  const isExpert = user?.role === 'EXPERT';

  // ── Basic profile ────────────────────────────────────────────
  const [profile, setProfile] = useState({
    firstName:    user?.firstName ?? '',
    lastName:     user?.lastName  ?? '',
    phone:        user?.phone     ?? '',
    businessName: '',
    businessType: '',
    city:         '',
  });
  const [savingProfile, setSavingProfile] = useState(false);

  // ── Expert profile ───────────────────────────────────────────
  const [expertProfile, setExpertProfile] = useState({
    bio:               '',
    hourlyRate:        '',
    currency:          'GHS',
    timezone:          'Africa/Accra',
    linkedinUrl:       '',
    websiteUrl:        '',
    yearsOfExperience: '',
    specializations:   [],
    qualifications:    [],
    sessionTypes:      ['VIDEO', 'AUDIO', 'CHAT'],
  });
  const [savingExpert,  setSavingExpert]  = useState(false);
  const [loadingExpert, setLoadingExpert] = useState(false);

  // ── Availability ─────────────────────────────────────────────
  const [availability, setAvailability] = useState(defaultSlots);
  const [savingAvail,  setSavingAvail]  = useState(false);

  // ── Password ─────────────────────────────────────────────────
  const [pwd,       setPwd]     = useState({ current: '', new: '', confirm: '' });
  const [showPwd,   setShowPwd] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // ── Promoted-but-incomplete banner ──────────────────────────
  const [profileIncomplete, setProfileIncomplete] = useState(false);
  const [bannerDismissed,   setBannerDismissed]   = useState(false);

  const upd = (setter, key) => (e) => setter((p) => ({ ...p, [key]: e.target.value }));

  // Toggle a session type on/off — must keep at least one
  const toggleSessionType = (type) => {
    setExpertProfile(p => {
      const current = p.sessionTypes ?? ['VIDEO', 'AUDIO', 'CHAT'];
      const next    = current.includes(type)
        ? current.filter(t => t !== type)
        : [...current, type];
      return next.length > 0 ? { ...p, sessionTypes: next } : p;
    });
  };

  // ── Fetch expert profile on mount ────────────────────────────
  useEffect(() => {
    if (!isExpert) return;
    const fetchExpert = async () => {
      setLoadingExpert(true);
      try {
        const res    = await expertService.getMyProfile();
        const expert = res.data?.data?.profile ?? res.data?.data?.expert ?? res.data?.expert ?? res.data?.data ?? res.data;
        if (expert) {
          setExpertProfile({
            bio:               expert.bio               ?? '',
            hourlyRate:        expert.hourlyRate         ?? '',
            currency:          expert.currency           ?? 'GHS',
            timezone:          expert.timezone           ?? 'Africa/Accra',
            linkedinUrl:       expert.linkedinUrl        ?? '',
            websiteUrl:        expert.websiteUrl         ?? '',
            yearsOfExperience: expert.yearsOfExperience  ?? '',
            specializations:   parseJson(expert.specializations, []),
            qualifications:    parseJson(expert.qualifications, []),
            sessionTypes:      parseJson(expert.sessionTypes, ['VIDEO', 'AUDIO', 'CHAT']),
          });
          setAvailability(parseAvailability(expert.availableFrom));
          setProfileIncomplete(!expert.bio || expert.bio.trim() === '');
        }
      } catch (e) {
        console.error('Could not load expert profile:', e.message);
      } finally {
        setLoadingExpert(false);
      }
    };
    fetchExpert();
  }, [isExpert]);

  // ── Fetch client profile on mount ────────────────────────────
  useEffect(() => {
    if (isExpert) return;
    const fetchClient = async () => {
      try {
        const res    = await userService.getProfile();
        const data   = res.data?.data ?? res.data;
        const user   = data?.user ?? data;
        const client = user?.clientProfile ?? data?.clientProfile ?? data;
        if (client) {
          setProfile(prev => ({
            ...prev,
            businessName: client.businessName ?? '',
            businessType: client.businessType ?? '',
            city:         client.city         ?? '',
          }));
        }
      } catch (e) {
        console.error('Could not load client profile:', e.message);
      }
    };
    fetchClient();
  }, [isExpert]);

  // ── Save handlers ────────────────────────────────────────────
  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await userService.updateProfile({
        firstName: profile.firstName,
        lastName:  profile.lastName,
        phone:     profile.phone,
      });
      if (!isExpert) {
        await userService.updateClientBusiness({
          businessName: profile.businessName,
          businessType: profile.businessType,
          city:         profile.city,
          country:      'Ghana',
        });
      }
      toast.success('Profile updated!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save profile');
    } finally { setSavingProfile(false); }
  };

  const saveExpertProfile = async () => {
    setSavingExpert(true);
    try {
      await expertService.updateMyProfile({
        bio:               expertProfile.bio,
        hourlyRate:        Number(expertProfile.hourlyRate),
        currency:          expertProfile.currency,
        timezone:          expertProfile.timezone,
        linkedinUrl:       expertProfile.linkedinUrl,
        websiteUrl:        expertProfile.websiteUrl,
        yearsOfExperience: Number(expertProfile.yearsOfExperience),
        specializations:   expertProfile.specializations,
        qualifications:    expertProfile.qualifications,
        sessionTypes:      expertProfile.sessionTypes,
      });
      toast.success('Expert profile updated!');
      if (expertProfile.bio?.trim()) {
        setProfileIncomplete(false);
        setBannerDismissed(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save expert profile');
    } finally { setSavingExpert(false); }
  };

  const saveAvailability = async () => {
    setSavingAvail(true);
    try {
      await expertService.updateAvailability(availability);
      toast.success('Availability updated!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save availability');
    } finally { setSavingAvail(false); }
  };

  const addSlot    = (day) => setAvailability((a) => ({ ...a, [day]: [...(a[day] ?? []), { start: '09:00', end: '17:00' }] }));
  const removeSlot = (day, idx) => setAvailability((a) => ({ ...a, [day]: (a[day] ?? []).filter((_, i) => i !== idx) }));
  const updateSlot = (day, idx, key, val) => setAvailability((a) => {
    const slots = [...(a[day] ?? [])];
    slots[idx]  = { ...slots[idx], [key]: val };
    return { ...a, [day]: slots };
  });

  const changePassword = async () => {
    if (pwd.new !== pwd.confirm) { toast.error('New passwords do not match'); return; }
    if (pwd.new.length < 8)      { toast.error('Password must be at least 8 characters'); return; }
    setSavingPwd(true);
    try {
      await authService.changePassword(pwd.current, pwd.new);
      toast.success('Password changed!');
      setPwd({ current: '', new: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not change password');
    } finally { setSavingPwd(false); }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append('avatar', file);
      await (isExpert ? expertService.uploadAvatar(fd) : userService.uploadAvatar(fd));
      toast.success('Avatar updated! Refresh to see changes.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not upload avatar');
    } finally { setUploadingAvatar(false); e.target.value = ''; }
  };

  // ── Tag helpers ───────────────────────────────────────────────
  const [specInput, setSpecInput] = useState('');
  const [qualInput, setQualInput] = useState('');

  const addSpec    = () => { const v = specInput.trim(); if (!v) return; setExpertProfile(p => ({ ...p, specializations: [...p.specializations, v] })); setSpecInput(''); };
  const removeSpec = (i) => setExpertProfile(p => ({ ...p, specializations: p.specializations.filter((_, idx) => idx !== i) }));
  const addQual    = () => { const v = qualInput.trim(); if (!v) return; setExpertProfile(p => ({ ...p, qualifications: [...p.qualifications, v] })); setQualInput(''); };
  const removeQual = (i) => setExpertProfile(p => ({ ...p, qualifications: p.qualifications.filter((_, idx) => idx !== i) }));

  const initials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`;

  return (
    <DashboardLayout title="Settings">
      <div className="max-w-2xl space-y-6">

        {/* Promoted-but-incomplete banner */}
        {isExpert && profileIncomplete && !bannerDismissed && (
          <div className="flex gap-4 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4">
            <AlertTriangle size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-800">Complete your expert profile</p>
              <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
                Your account was recently upgraded to Expert. Clients can't book you until your profile is filled in.
                Please add your <strong>bio</strong>, <strong>specializations</strong>, <strong>qualifications</strong> below.
              </p>
            </div>
            <button onClick={() => setBannerDismissed(true)}
              className="text-amber-400 hover:text-amber-600 transition-colors flex-shrink-0">
              <X size={15} />
            </button>
          </div>
        )}

        {/* Profile Photo */}
        <Section title="Profile Photo">
          <div className="flex items-center gap-5">
            <div className="relative">
              <div className="w-20 h-20 bg-forest-700 rounded-2xl flex items-center justify-center text-cream-50 font-serif font-bold text-2xl overflow-hidden">
                {user?.avatar
                  ? <img src={user.avatar} alt="" className="w-full h-full object-cover" />
                  : initials}
              </div>
              <button onClick={() => fileRef.current?.click()}
                className="absolute -bottom-2 -right-2 w-8 h-8 bg-forest-800 border-2 border-white rounded-full flex items-center justify-center text-cream-50 hover:bg-forest-700 transition-colors">
                {uploadingAvatar ? <Loader2 size={12} className="animate-spin" /> : <Camera size={12} />}
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} />
            </div>
            <div>
              <p className="text-sm font-medium text-forest-800">{user?.firstName} {user?.lastName}</p>
              <p className="text-xs text-forest-500 mt-0.5 capitalize">{user?.role?.toLowerCase().replace('_', ' ')}</p>
              <p className="text-xs text-forest-400 mt-1">JPEG, PNG or WEBP · Max 5MB</p>
            </div>
          </div>
        </Section>

        {/* Basic Info */}
        <Section title="Basic Information">
          <div className="grid grid-cols-2 gap-4">
            <Field label="First Name" value={profile.firstName} onChange={upd(setProfile, 'firstName')} placeholder="John" />
            <Field label="Last Name"  value={profile.lastName}  onChange={upd(setProfile, 'lastName')}  placeholder="Mensah" />
          </div>
          <Field label="Phone" type="tel" value={profile.phone} onChange={upd(setProfile, 'phone')} placeholder="+233244000001" />

          {!isExpert && (
            <>
              <Field label="Enterprise Name" value={profile.businessName} onChange={upd(setProfile, 'businessName')} placeholder="Accra Tours Ltd" />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-forest-700 mb-1.5">Enterprise Type</label>
                  <select
                    value={profile.businessType}
                    onChange={upd(setProfile, 'businessType')}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white">
                    <option value="">Select type...</option>
                    {ENTERPRISE_TYPES.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <Field label="City" value={profile.city} onChange={upd(setProfile, 'city')} placeholder="Accra" />
              </div>
            </>
          )}

          <button onClick={saveProfile} disabled={savingProfile} className="btn-primary flex items-center gap-2 disabled:opacity-60">
            {savingProfile ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Changes
          </button>
        </Section>

        {/* Expert Profile */}
        {isExpert && (
          <div className={profileIncomplete && !bannerDismissed ? 'ring-2 ring-amber-300 ring-offset-2 rounded-2xl' : ''}>
          <Section title="Expert Profile">
            {loadingExpert ? (
              <div className="flex items-center gap-2 text-forest-400 py-4">
                <Loader2 size={16} className="animate-spin" />
                <span className="text-sm">Loading your profile...</span>
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-xs font-medium text-forest-700 mb-1.5">Bio</label>
                  <textarea value={expertProfile.bio} onChange={upd(setExpertProfile, 'bio')} rows={5}
                    placeholder="Describe your expertise..."
                    className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white resize-none" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-forest-700 mb-1.5">Hourly Rate</label>
                    <input type="number" value={expertProfile.hourlyRate ?? ''} placeholder="75" disabled
                      className="w-full px-3.5 py-2.5 rounded-xl border border-cream-200 text-sm text-forest-400 bg-cream-50 cursor-not-allowed" />
                    <p className="text-xs text-forest-400 mt-1">Set by admin</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-forest-700 mb-1.5">Currency</label>
                    <select value={expertProfile.currency} disabled
                      className="w-full px-3.5 py-2.5 rounded-xl border border-cream-200 text-sm text-forest-400 bg-cream-50 cursor-not-allowed">
                      <option value="GHS">GHS (GH₵)</option>
                      <option value="USD">USD ($)</option>
                    </select>
                    <p className="text-xs text-forest-400 mt-1">Set by admin</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Years of Experience" type="number" value={expertProfile.yearsOfExperience} onChange={upd(setExpertProfile, 'yearsOfExperience')} placeholder="5" />
                  <Field label="Timezone" value={expertProfile.timezone} onChange={upd(setExpertProfile, 'timezone')} placeholder="Africa/Accra" />
                </div>
                <Field label="LinkedIn URL" type="url" value={expertProfile.linkedinUrl} onChange={upd(setExpertProfile, 'linkedinUrl')} placeholder="https://linkedin.com/in/..." />
                <Field label="Website URL"  type="url" value={expertProfile.websiteUrl}  onChange={upd(setExpertProfile, 'websiteUrl')}  placeholder="https://yoursite.com" />

                {/* ── Session Types ── */}
                <div>
                  <label className="block text-xs font-medium text-forest-700 mb-1">Session Types Offered</label>
                  <p className="text-xs text-forest-400 mb-3">Select which types of sessions you're available for. At least one required.</p>
                  <div className="grid grid-cols-3 gap-3">
                    {SESSION_TYPE_OPTIONS.map(({ id, icon: Icon, label, desc }) => {
                      const active = (expertProfile.sessionTypes ?? ['VIDEO', 'AUDIO', 'CHAT']).includes(id);
                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => toggleSessionType(id)}
                          className={`flex flex-col items-center gap-2 py-4 px-2 rounded-xl border-2 text-center transition-all ${
                            active
                              ? 'border-forest-700 bg-forest-50'
                              : 'border-cream-200 bg-white hover:border-forest-300 opacity-50'
                          }`}
                        >
                          <Icon size={20} className={active ? 'text-forest-700' : 'text-forest-400'} />
                          <div>
                            <p className={`text-xs font-semibold ${active ? 'text-forest-900' : 'text-forest-500'}`}>{label}</p>
                            <p className="text-[10px] text-forest-400 mt-0.5 leading-tight">{desc}</p>
                          </div>
                          {active && (
                            <span className="text-[9px] font-bold text-forest-600 bg-forest-100 px-2 py-0.5 rounded-full">ON</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Specializations */}
                <div>
                  <label className="block text-xs font-medium text-forest-700 mb-1.5">Specializations</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {expertProfile.specializations.map((s, i) => (
                      <span key={i} className="flex items-center gap-1.5 text-xs bg-forest-50 text-forest-700 border border-forest-100 px-3 py-1 rounded-full">
                        {s}
                        <button type="button" onClick={() => removeSpec(i)} className="text-forest-300 hover:text-rust-500 transition-colors">✕</button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input value={specInput} onChange={e => setSpecInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addSpec())}
                      placeholder="e.g. Heritage Tourism"
                      className="flex-1 px-3.5 py-2 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500" />
                    <button type="button" onClick={addSpec}
                      className="px-4 py-2 rounded-xl bg-forest-800 text-cream-50 text-xs font-medium hover:bg-forest-700 transition-colors">Add</button>
                  </div>
                </div>

                {/* Qualifications */}
                <div>
                  <label className="block text-xs font-medium text-forest-700 mb-1.5">Qualifications</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {expertProfile.qualifications.map((q, i) => (
                      <span key={i} className="flex items-center gap-1.5 text-xs bg-cream-100 text-forest-700 border border-cream-200 px-3 py-1 rounded-full">
                        {q}
                        <button type="button" onClick={() => removeQual(i)} className="text-forest-300 hover:text-rust-500 transition-colors">✕</button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input value={qualInput} onChange={e => setQualInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addQual())}
                      placeholder="e.g. MSc Tourism Management"
                      className="flex-1 px-3.5 py-2 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500" />
                    <button type="button" onClick={addQual}
                      className="px-4 py-2 rounded-xl bg-forest-800 text-cream-50 text-xs font-medium hover:bg-forest-700 transition-colors">Add</button>
                  </div>
                </div>

                <button onClick={saveExpertProfile} disabled={savingExpert} className="btn-primary flex items-center gap-2 disabled:opacity-60">
                  {savingExpert ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Expert Profile
                </button>
              </>
            )}
          </Section>
          </div>
        )}

        {/* Availability */}
        {isExpert && (
          <Section title="Weekly Availability">
            <p className="text-xs text-forest-500 -mt-2">Set your available time slots. Leave a day empty to mark as unavailable.</p>
            <div className="space-y-3">
              {DAYS.map((day) => (
                <div key={day} className="border border-cream-100 rounded-xl p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-forest-800 capitalize">{day}</span>
                    <button type="button" onClick={() => addSlot(day)} className="text-xs text-forest-600 hover:text-forest-900 font-medium">+ Add slot</button>
                  </div>
                  {(availability[day] ?? []).length > 0 ? (
                    <div className="space-y-2">
                      {(availability[day] ?? []).map((slot, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <input type="time" value={slot.start} onChange={(e) => updateSlot(day, idx, 'start', e.target.value)}
                            className="px-2.5 py-1.5 rounded-lg border border-cream-200 text-xs text-forest-800 focus:outline-none focus:border-forest-400" />
                          <span className="text-xs text-forest-400">to</span>
                          <input type="time" value={slot.end} onChange={(e) => updateSlot(day, idx, 'end', e.target.value)}
                            className="px-2.5 py-1.5 rounded-lg border border-cream-200 text-xs text-forest-800 focus:outline-none focus:border-forest-400" />
                          <button type="button" onClick={() => removeSlot(day, idx)} className="text-forest-300 hover:text-rust-500 transition-colors ml-1">✕</button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-forest-300 italic">Not available</p>
                  )}
                </div>
              ))}
            </div>
            <button onClick={saveAvailability} disabled={savingAvail} className="btn-primary flex items-center gap-2 disabled:opacity-60">
              {savingAvail ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Availability
            </button>
          </Section>
        )}

        {/* Change Password */}
        <Section title="Change Password">
          <div className="relative">
            <label className="block text-xs font-medium text-forest-700 mb-1.5">Current Password</label>
            <input type={showPwd ? 'text' : 'password'} value={pwd.current} onChange={(e) => setPwd((p) => ({ ...p, current: e.target.value }))}
              placeholder="••••••••"
              className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white pr-10" />
            <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-8 text-forest-400 hover:text-forest-700">
              {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="New Password"     type={showPwd ? 'text' : 'password'} value={pwd.new}     onChange={(e) => setPwd((p) => ({ ...p, new: e.target.value }))}     placeholder="••••••••" />
            <Field label="Confirm Password" type={showPwd ? 'text' : 'password'} value={pwd.confirm} onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))} placeholder="••••••••" />
          </div>
          <button onClick={changePassword} disabled={savingPwd || !pwd.current || !pwd.new} className="btn-primary flex items-center gap-2 disabled:opacity-60">
            {savingPwd ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />} Change Password
          </button>
        </Section>

      </div>
    </DashboardLayout>
  );
};

export default SettingsPage;